import {
  adminSiteTextSchema,
  contentRevisionSchema,
  SITE_TEXTS,
  type AdminSiteText,
  type ContentRevision,
  type SiteTextDef,
} from '@growup/site-shared';
import { Button, Card, Field, Input, Notice, Textarea } from '@growup/ui';
import { useCallback, useEffect, useState } from 'react';
import { z } from 'zod';

import { ApiError, getJson, messageOf, send } from '../api';
import { formatDateTime } from '../format';

const PAGES = [...new Set(SITE_TEXTS.map((d) => d.page))];

/** Textos das paginas (Inicio, Sobre nos): a disposicao fica, o texto muda. */
export function TextsView() {
  const [texts, setTexts] = useState<AdminSiteText[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    getJson('/api/site/content/texts', z.array(adminSiteTextSchema))
      .then(setTexts)
      .catch((e: unknown) => setError(messageOf(e)));
  }, []);
  useEffect(load, [load]);

  const replace = (saved: AdminSiteText) =>
    setTexts((list) => list?.map((t) => (t.key === saved.key ? saved : t)) ?? null);

  return (
    <div className="flex flex-col gap-4">
      <Notice tone="info">
        Cada texto aparece no site logo que é guardado. «Repor» volta ao texto de origem; as versões
        anteriores ficam em «Histórico».
      </Notice>
      {error ? <Notice tone="danger">{error}</Notice> : null}
      {texts === null && !error ? <p>A carregar…</p> : null}
      {texts
        ? PAGES.map((page) => (
            <Card key={page} headingLevel={2} title={page}>
              <ul className="flex flex-col gap-5">
                {SITE_TEXTS.filter((d) => d.page === page).map((def) => {
                  const current = texts.find((t) => t.key === def.key);
                  return current ? (
                    <li key={def.key}>
                      <TextRow def={def} text={current} onSaved={replace} />
                    </li>
                  ) : null;
                })}
              </ul>
            </Card>
          ))
        : null}
    </div>
  );
}

function TextRow({
  def,
  text,
  onSaved,
}: {
  def: SiteTextDef;
  text: AdminSiteText;
  onSaved: (t: AdminSiteText) => void;
}) {
  const [value, setValue] = useState(text.text);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState<ContentRevision[] | null>(null);
  const changed = value !== text.text;

  const save = async (next: string | null) => {
    setBusy(true);
    setError(null);
    try {
      const saved = adminSiteTextSchema.parse(
        await send('PUT', `/api/site/content/texts/${def.key}`, { text: next }),
      );
      onSaved(saved);
      setValue(saved.text);
      setHistory(null);
    } catch (e) {
      setError(e instanceof ApiError ? (e.fields.text ?? e.message) : messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  const showHistory = async () => {
    try {
      setHistory(
        z
          .array(contentRevisionSchema)
          .parse(await getJson(`/api/site/content/texts/${def.key}/revisions`, z.unknown())),
      );
    } catch (e) {
      setError(messageOf(e));
    }
  };

  const hint = (
    <>
      {value.length}/{def.max}
      {def.multiline ? ' · Parágrafos separados por uma linha em branco.' : ''}
      {text.edited && text.updatedAt
        ? ` · Mudado a ${formatDateTime(text.updatedAt)} por ${text.updatedBy ?? '—'}.`
        : ' · Texto de origem.'}
    </>
  );

  return (
    <div className="flex flex-col gap-2">
      <Field label={def.label} hint={hint} error={error ?? undefined}>
        {def.multiline || def.max > 120 ? (
          <Textarea
            rows={def.multiline ? 5 : 2}
            maxLength={def.max}
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
        ) : (
          <Input maxLength={def.max} value={value} onChange={(e) => setValue(e.target.value)} />
        )}
      </Field>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" disabled={busy || !changed} onClick={() => void save(value)}>
          Guardar
        </Button>
        {changed ? (
          <Button size="sm" variant="ghost" onClick={() => setValue(text.text)}>
            Desfazer
          </Button>
        ) : null}
        {text.edited ? (
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => void save(null)}>
            Repor o texto de origem
          </Button>
        ) : null}
        <Button
          size="sm"
          variant="ghost"
          onClick={() => void (history ? setHistory(null) : showHistory())}
        >
          {history ? 'Fechar histórico' : 'Histórico'}
        </Button>
      </div>
      {history ? (
        history.length === 0 ? (
          <p className="text-caption text-gu-ink-muted">Ainda não há versões anteriores.</p>
        ) : (
          <ol className="flex flex-col gap-2 border-l-2 border-gu-line pl-3">
            {history.map((r) => {
              const old = z.object({ text: z.string() }).safeParse(r.content);
              return (
                <li key={r.id} className="flex flex-col gap-1">
                  <span className="text-caption text-gu-ink-muted">
                    {r.createdBy === 'texto inicial'
                      ? 'Texto de origem'
                      : `${formatDateTime(r.createdAt)} · ${r.createdBy}`}
                  </span>
                  <span className="whitespace-pre-line">{old.success ? old.data.text : '—'}</span>
                  {old.success ? (
                    <span>
                      <Button size="sm" variant="ghost" onClick={() => setValue(old.data.text)}>
                        Usar este texto
                      </Button>
                    </span>
                  ) : null}
                </li>
              );
            })}
          </ol>
        )
      ) : null}
    </div>
  );
}
